import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Global } from './Global';
import { Observable, Subject } from 'rxjs';

export interface AccessJoinPayload {
  userId?: number | string | null;
  roleId?: number | string | null;
  entityCodes?: Array<number | string>;
}

export interface AccessChangedEvent {
  type: string;
  reason?: string;
  forceLogout?: boolean;
  roleId?: number | string | null;
  userId?: number | string | null;
  entityCode?: number | string | null;
  timestamp?: string | Date;
}

export interface CatalogChangedEvent {
  scopes?: string[];
  /** true = sync aplicó a solicitudes abiertas; false = solo catálogo */
  applyToOpen?: boolean;
  processSlug?: string;
  timestamp?: string | Date;
}

@Injectable({ providedIn: 'root' })
export class SocketService {
  private socket: Socket;
  private notificationSubject = new Subject<any>();
  private accessChangedSubject = new Subject<AccessChangedEvent>();
  private catalogChangedSubject = new Subject<CatalogChangedEvent>();
  private sessionSyncSubject = new Subject<{ uuid: string }>();
  private browserSessionSubject = new Subject<'login' | 'logout' | 'context'>();
  private browserIdValue: string | null = null;
  private connectionStatus$ = new Subject<boolean>();
  private rooms = new Set<number | string>();
  private accessJoin: AccessJoinPayload | null = null;

  constructor() {
    const baseUrl = Global.url.replace('/api/', '/').replace('/V1/', '/');

    this.socket = io(baseUrl, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      upgrade: true,
      withCredentials: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      timeout: 20000
    });

    this.socket.on('connect', () => {
      this.connectionStatus$.next(true);
      this.rooms.forEach(room => this.socket.emit('join', room));
      if (this.accessJoin) {
        this.socket.emit('joinAccess', this.accessJoin);
      }
      this.joinBrowser();
    });

    this.socket.on('notification', (data) => {
      const normalizedData = {
        ...data,
        type: data.type || (data.data?.type) || 'INFO'
      };
      this.notificationSubject.next(normalizedData);
    });

    this.socket.on('accessChanged', (data: AccessChangedEvent) => {
      this.accessChangedSubject.next(data || { type: 'ACCESS_CHANGED' });
    });

    this.socket.on('catalogChanged', (data: CatalogChangedEvent) => {
      this.catalogChangedSubject.next(data || { scopes: ['all'] });
    });

    this.socket.on('sessionSync', (data: { uuid: string }) => {
      if (data?.uuid) this.sessionSyncSubject.next(data);
    });

    this.socket.on('browserSession', (data: { action?: string }) => {
      if (data?.action === 'login' || data?.action === 'logout' || data?.action === 'context') {
        this.browserSessionSubject.next(data.action);
      }
    });

    this.socket.on('disconnect', () => {
      this.connectionStatus$.next(false);
    });
  }

  join(entityCode: number | string) {
    if (entityCode) {
      this.rooms.add(entityCode);
      this.socket.emit('join', entityCode);
    }
  }

  /** Salas de sesión: user / role / entities */
  joinAccess(payload: AccessJoinPayload) {
    this.accessJoin = {
      userId: payload?.userId ?? null,
      roleId: payload?.roleId ?? null,
      entityCodes: [...(payload?.entityCodes || [])]
    };
    this.socket.emit('joinAccess', this.accessJoin);
  }

  private browserId(): string {
    if (this.browserIdValue) return this.browserIdValue;
    const key = 'fpv_browser_id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    this.browserIdValue = id;
    return id;
  }

  joinBrowser() {
    this.socket.emit('joinBrowser', this.browserId());
  }

  announceBrowserSession(action: 'login' | 'logout' | 'context') {
    this.joinBrowser();
    this.socket.emit('browserSession', { action });
  }

  onBrowserSession(): Observable<'login' | 'logout' | 'context'> {
    return this.browserSessionSubject.asObservable();
  }

  joinSession(uuid: string) {
    if (uuid) this.socket.emit('joinSession', uuid);
  }

  onSessionSync(): Observable<{ uuid: string }> {
    return this.sessionSyncSubject.asObservable();
  }

  test(entityCode: number | string) {
    if (entityCode) {
      this.socket.emit('test', entityCode);
    }
  }

  onNotification(): Observable<any> {
    return this.notificationSubject.asObservable();
  }

  onAccessChanged(): Observable<AccessChangedEvent> {
    return this.accessChangedSubject.asObservable();
  }

  onCatalogChanged(): Observable<CatalogChangedEvent> {
    return this.catalogChangedSubject.asObservable();
  }

  onConnectionStatus(): Observable<boolean> {
    return this.connectionStatus$.asObservable();
  }
}
